import { useState } from "react";
import { api, formatApiErrorDetail, STATUS_LABELS, STATUS_COLORS, SERVICE_OPTIONS } from "@/lib/api";
import { Search } from "lucide-react";

const SERVICE_MAP = Object.fromEntries(SERVICE_OPTIONS.map((s) => [s.value, s.label]));

export default function TrackBooking() {
  const [ref, setRef] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.get(`/bookings/lookup/${encodeURIComponent(ref.trim())}`);
      setResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Lookup failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div data-testid="track-page" className="max-w-2xl mx-auto px-4 sm:px-8 py-16 sm:py-24">
      <span className="font-mono text-xs uppercase tracking-widest text-orange-500">— Track</span>
      <h1 className="font-display text-5xl sm:text-6xl uppercase mt-2 mb-6">Where's My Bike?</h1>
      <p className="text-zinc-400 mb-10">
        Enter the KPMX reference we emailed you to see the current workshop status.
      </p>

      <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3 mb-8">
        <input required value={ref} onChange={(e) => setRef(e.target.value)}
          placeholder="KPMX-XXXXXX" data-testid="track-input"
          className="flex-1 bg-zinc-950 border border-white/10 focus:border-orange-500 focus:ring-2 focus:ring-orange-600/30 outline-none px-4 py-3 text-white font-mono uppercase tracking-widest" />
        <button type="submit" disabled={loading} data-testid="track-submit"
          className="btn-hard inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-500 text-white px-6 py-3 font-display uppercase tracking-widest text-sm disabled:opacity-50">
          <Search size={16} /> {loading ? "Searching..." : "Look Up"}
        </button>
      </form>

      {error && <div data-testid="track-error" className="border border-red-500/40 bg-red-500/10 text-red-300 p-4 mb-6 text-sm">{error}</div>}

      {result && (
        <div data-testid="track-result" className="border border-white/10 bg-zinc-900/40 p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="font-mono text-xs uppercase text-zinc-500">Reference</div>
              <div className="font-display text-3xl text-orange-500">{result.reference}</div>
            </div>
            <span data-testid="track-status-badge" className={`px-3 py-1.5 text-sm border ${STATUS_COLORS[result.status]}`}>
              {STATUS_LABELS[result.status]}
            </span>
          </div>
          <dl className="space-y-4 text-sm">
            <Row label="Service" value={SERVICE_MAP[result.service_type]} />
            <Row label="Collection" value={result.collection_method === "courier_box" ? "Courier box" : "We collect"} />
            <Row label="Bike" value={result.bike} />
            <Row label="Booked" value={new Date(result.created_at).toLocaleDateString()} />
            <Row label="Last update" value={new Date(result.updated_at).toLocaleString()} />
          </dl>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-3 border-b border-white/10 pb-3">
      <dt className="font-mono text-xs uppercase tracking-widest text-zinc-500">{label}</dt>
      <dd className="col-span-2 text-white">{value || "—"}</dd>
    </div>
  );
}
